/*
 * Platform Analytics Experience — governed design fixtures.
 *
 * Unlike the Cross-Platform Analytics page (which ships fixture-first
 * because it has no per-day live contract at all), the individual platform
 * pages default to LIVE topline data — these fixtures exist only for:
 *   1. the explicit, labeled design preview (?fixture=<state>), which is the
 *      only place a daily delivery series can currently be shown, because
 *      the governed daily time-series backend contract does not exist yet;
 *   2. deterministic tests across every required data state.
 *
 * Every fixture dataset carries `source.kind: "design_fixture"` and the page
 * renders a persistent badge whenever the source is not live — fixture
 * values can never be mistaken for canonical reporting.
 *
 * Deterministic (seeded PRNG, no Math.random) and evergreen (dates computed
 * relative to "now", never hardcoded).
 */

export const PLATFORM_ANALYTICS_FIXTURE_STATES = [
  { key: "full", label: "Full delivery" },
  { key: "partial", label: "Partial metrics" },
  { key: "stale", label: "Stale ingested source" },
  { key: "no_data", label: "No delivery" },
  { key: "connector_error", label: "Source unavailable" },
];

function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function toDateKey(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDays(date, days) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/* Per-platform delivery personality for the design preview series. */
const FIXTURE_PROFILES = {
  tiktok: { dailySpend: 1450, cpm: 6.8, ctr: 0.0118, videoRate: 0.55, completionOfViews: 0.52, engagementRate: 0.032, conversions: false },
  google_search: { dailySpend: 1900, cpm: 21.5, ctr: 0.041, videoRate: 0, completionOfViews: 0, engagementRate: 0.008, conversions: true, conversionRate: 0.062 },
  youtube: { dailySpend: 1600, cpm: 12.4, ctr: 0.0042, videoRate: 0.62, completionOfViews: 0.56, engagementRate: 0.006, conversions: false },
  x: { dailySpend: 620, cpm: 5.9, ctr: 0.009, videoRate: 0.34, completionOfViews: 0.4, engagementRate: 0.021, conversions: false },
  reddit: { dailySpend: 410, cpm: 4.2, ctr: 0.0064, videoRate: 0, completionOfViews: 0, engagementRate: 0.017, conversions: false },
  amazon: { dailySpend: 980, cpm: 9.1, ctr: 0.0155, videoRate: 0, completionOfViews: 0, engagementRate: 0, conversions: true, conversionRate: 0.084 },
  linkedin: { dailySpend: 760, cpm: 31.5, ctr: 0.0052, videoRate: 0, completionOfViews: 0, engagementRate: 0.011, conversions: true, conversionRate: 0.048, leadsRate: 0.031 },
  shopify: { dailySpend: 0, cpm: 0, ctr: 0, videoRate: 0, completionOfViews: 0, engagementRate: 0, conversions: false, commerce: true, dailyRevenue: 5400, avgOrderValue: 84, newCustomerShare: 0.46 },
  truthsocial: { dailySpend: 180, cpm: 3.4, ctr: 0.0071, videoRate: 0.3, completionOfViews: 0.44, engagementRate: 0.026, conversions: false },
  rumble: { dailySpend: 260, cpm: 4.8, ctr: 0.0038, videoRate: 0.58, completionOfViews: 0.49, engagementRate: 0.012, conversions: false },
  chatgpt_search: { dailySpend: 340, cpm: 17.2, ctr: 0.023, videoRate: 0, completionOfViews: 0, engagementRate: 0, conversions: true, conversionRate: 0.041 },
  metricool: { dailySpend: 0, cpm: 2.6, ctr: 0.0058, videoRate: 0.22, completionOfViews: 0.41, engagementRate: 0.03, conversions: false, organic: true, dailyImpressions: 46000 },
};

const DEFAULT_FIXTURE_PROFILE = FIXTURE_PROFILES.tiktok;

function fixtureSeedForPlatform(platformKey) {
  const text = String(platformKey || "");
  let seed = 19;
  for (let index = 0; index < text.length; index += 1) {
    seed = (seed * 31 + text.charCodeAt(index)) >>> 0;
  }
  return seed;
}

/*
 * Fixture audience-synthesis payloads, shaped exactly like the canonical
 * GET /api/audience-intelligence/:advertiserId resolution so the page runs
 * them through the same view model as live records. Fixture-only content is
 * explicitly labeled; states cover platform-specific, shared-fallback, aged,
 * absent, and errored synthesis.
 */
function buildFixtureAudience(platformKey, stateKey, { windowLabel, now }) {
  if (stateKey === "connector_error") {
    return { requestState: "error", resolution: null };
  }
  if (stateKey === "no_data") {
    return {
      requestState: "resolved",
      resolution: { ok: true, tier: "unavailable", source: "none", card: null },
    };
  }

  const shared = stateKey === "partial";
  const aged = stateKey === "stale";
  const updatedAt = new Date(now.getTime() - (aged ? 60 : 2) * 86400000).toISOString();
  const label = String(platformKey || "platform").replace(/_/g, " ");

  return {
    requestState: "resolved",
    resolution: {
      ok: true,
      tier: "snapshot",
      source: "manual",
      isFallback: true,
      platform: shared ? "overall" : platformKey,
      confidence: "directional",
      dateLabel: windowLabel,
      updatedAt,
      audienceSummary: shared
        ? "Fixture shared synthesis: the campaign audience skews 25–44, responds to short-form storytelling, and concentrates in the top ten launch markets."
        : `Fixture ${label} synthesis: the responding audience skews 25–44, engages most in evening sessions, and moves from awareness into retargetable engagement within the launch window.`,
      primaryAudienceNarrative: shared
        ? "Adults 25–44 in launch markets showing repeat exposure response across governed channels."
        : `Adults 25–44 engaging with ${label} placements, with strongest response after repeat exposure and evening delivery.`,
      strongestSegments: [
        "Fixture segment — adults 25–34 in top launch markets",
        "Fixture segment — repeat engagers within 7 days",
        "Fixture segment — evening high-attention sessions",
      ],
      emergingSegments: [
        "Fixture segment — adults 35–44 responding to narrative creative",
        "Fixture segment — secondary-market weekend viewers",
      ],
      recommendedAction:
        "Fixture guidance: hold current audience weighting and review again after the next governed analysis run.",
      card: {
        available: true,
        summary: shared
          ? "Fixture shared synthesis: one governed audience read applies across platforms; no platform-specific interpretation exists for this source."
          : `Fixture ${label} synthesis: audience response is strongest among adults 25–44 with repeat evening exposure.`,
        bullets: [
          "Strongest segment: Fixture segment — adults 25–34 in top launch markets.",
          "Strongest segment: Fixture segment — repeat engagers within 7 days.",
          "Emerging segment: Fixture segment — adults 35–44 responding to narrative creative.",
          "Recommended action: Fixture guidance — hold current audience weighting and review after the next governed analysis run.",
        ],
        status: "Manual Intelligence",
        note: "Directional audience intelligence prepared for current client reporting.",
        disclaimer: "Design fixture — governed preview data, not a canonical audience record.",
        dateRangeLabel: windowLabel,
        sourceName: "Design fixture",
        sourceType: "manual_report",
        confidence: "directional",
      },
    },
  };
}

export function buildPlatformAnalyticsFixture(platformKey, stateKey = "full") {
  const normalizedState = PLATFORM_ANALYTICS_FIXTURE_STATES.some((state) => state.key === stateKey)
    ? stateKey
    : "full";
  const profile = FIXTURE_PROFILES[platformKey] || DEFAULT_FIXTURE_PROFILE;
  const rand = mulberry32(
    platformKey === "youtube" ? 77 : platformKey === "google_search" ? 41 : platformKey === "tiktok" ? 19 : fixtureSeedForPlatform(platformKey)
  );

  const days = 30;
  const today = new Date();
  const windowEnd = addDays(today, -1);
  const windowStart = addDays(windowEnd, -(days - 1));

  const series = [];
  const totals = {
    spend: 0, impressions: 0, clicks: 0,
    videoViews: 0, videoStarts: 0, videoCompletions: 0,
    engagements: 0, conversions: 0,
    revenue: 0, orders: 0, customers: 0, leads: 0,
  };

  if (normalizedState !== "no_data" && normalizedState !== "connector_error") {
    for (let dayIndex = days - 1; dayIndex >= 0; dayIndex -= 1) {
      const date = addDays(windowEnd, -dayIndex);
      const daysLive = days - 1 - dayIndex;
      const rampFactor = Math.min(1, 0.4 + daysLive * 0.11);
      const weekday = date.getDay();
      const weekdayFactor = weekday === 0 || weekday === 6 ? 0.85 : 1 + (weekday === 4 ? 0.07 : 0);
      const noise = 0.86 + rand() * 0.3;
      const drift = 1 + Math.sin(daysLive / 9) * 0.1;

      const spend = profile.dailySpend > 0 ? profile.dailySpend * rampFactor * weekdayFactor * noise * drift : null;
      const impressions = profile.organic
        ? profile.dailyImpressions * rampFactor * weekdayFactor * noise * drift
        : spend != null && profile.cpm > 0
        ? (spend / profile.cpm) * 1000 * (0.92 + rand() * 0.16)
        : null;
      const clicks = impressions != null && profile.ctr > 0 ? impressions * profile.ctr * (0.9 + rand() * 0.2) : null;
      const videoStarts =
        impressions != null && profile.videoRate > 0 ? impressions * profile.videoRate * (0.92 + rand() * 0.16) : null;
      const videoViews = videoStarts != null ? videoStarts * (0.9 + rand() * 0.08) : null;
      const videoCompletions = videoViews != null ? videoViews * profile.completionOfViews * (0.9 + rand() * 0.2) : null;
      const engagements =
        impressions != null && profile.engagementRate > 0
          ? impressions * profile.engagementRate * (0.88 + rand() * 0.24)
          : null;
      const conversions =
        clicks != null && profile.conversions ? clicks * profile.conversionRate * (0.85 + rand() * 0.3) : null;
      const revenue = profile.commerce ? profile.dailyRevenue * rampFactor * weekdayFactor * noise * drift : null;
      const orders = revenue != null ? revenue / (profile.avgOrderValue * (0.92 + rand() * 0.16)) : null;
      const customers = orders != null ? orders * profile.newCustomerShare * (0.9 + rand() * 0.2) : null;
      const leads = clicks != null && profile.leadsRate ? clicks * profile.leadsRate * (0.85 + rand() * 0.3) : null;

      const round = (value) => (value == null ? null : Math.round(value));
      const bucket = {
        date: toDateKey(date),
        metrics: {
          spend: spend == null ? null : Math.round(spend * 100) / 100,
          impressions: round(impressions),
          clicks: round(clicks),
          videoStarts: round(videoStarts),
          videoViews: round(videoViews),
          videoCompletions: round(videoCompletions),
          engagements: round(engagements),
          conversions: round(conversions),
          revenue: revenue == null ? null : Math.round(revenue * 100) / 100,
          orders: round(orders),
          customers: round(customers),
          leads: round(leads),
        },
      };
      series.push(bucket);
      Object.keys(totals).forEach((key) => {
        const value = bucket.metrics[key];
        if (value != null) totals[key] += value;
      });
    }
  }

  const hasDelivery = series.length > 0;
  const partial = normalizedState === "partial";
  const paidDelivery = profile.dailySpend > 0;
  const rawTotals = hasDelivery
    ? {
        spend: paidDelivery ? Math.round(totals.spend * 100) / 100 : null,
        impressions: profile.organic || profile.cpm > 0 ? totals.impressions : null,
        clicks: partial || profile.ctr <= 0 ? null : totals.clicks,
        ctr: partial || profile.ctr <= 0 || totals.impressions <= 0 ? null : (totals.clicks / totals.impressions) * 100,
        cpm: paidDelivery && totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : null,
        cpc: partial || !paidDelivery || totals.clicks <= 0 ? null : totals.spend / totals.clicks,
        cpa:
          paidDelivery && profile.conversions && totals.conversions > 0 ? totals.spend / totals.conversions : null,
        videoStarts: profile.videoRate > 0 ? totals.videoStarts : null,
        videoViews: profile.videoRate > 0 ? totals.videoViews : null,
        videoCompletions: partial ? null : profile.videoRate > 0 ? totals.videoCompletions : null,
        vcr:
          partial || profile.videoRate <= 0 || totals.videoStarts <= 0
            ? null
            : (totals.videoCompletions / totals.videoStarts) * 100,
        engagements: profile.engagementRate > 0 ? totals.engagements : null,
        conversions: profile.conversions ? totals.conversions : null,
        revenue: profile.commerce ? Math.round(totals.revenue * 100) / 100 : null,
        orders: profile.commerce ? totals.orders : null,
        aov: profile.commerce && totals.orders > 0 ? totals.revenue / totals.orders : null,
        customers: partial ? null : profile.commerce ? totals.customers : null,
        leads: profile.leadsRate ? (partial ? null : totals.leads) : null,
        sessions: null,
      }
    : {};

  const stale = normalizedState === "stale";
  const errored = normalizedState === "connector_error";

  const audience = buildFixtureAudience(platformKey, normalizedState, { windowLabel: "Last 30 days (fixture)", now: today });

  return {
    audience,
    state: normalizedState,
    source: {
      kind: "design_fixture",
      label: "Design fixture — governed preview data",
      tier: errored ? "unavailable" : stale ? "stored_snapshot" : "live_direct",
      sourceLabel: errored ? null : stale ? "Ingested" : "Live Feed",
      confidence: errored ? 0 : stale ? 0.45 : 0.95,
      /* Freshness is minutes-ago so the fixture stays evergreen. */
      freshnessMinutes: errored ? null : stale ? 3 * 24 * 60 : 55,
      isLive: !errored && !stale,
      isFallback: stale,
      notes: stale ? ["Fixture models a stored ingested snapshot, 3 days old."] : [],
      limitations: [
        "Design preview only — live daily delivery requires the governed daily time-series backend contract.",
      ],
    },
    window: { start: hasDelivery ? toDateKey(windowStart) : null, end: hasDelivery ? toDateKey(windowEnd) : null, label: "Last 30 days (fixture)" },
    series,
    rawTotals,
    pacing: hasDelivery
      ? { status: "on pace", meta: "Fixture pacing versus a 30-day even plan.", tone: "positive" }
      : { status: null, meta: null, tone: null },
    error: errored ? "Fixture models an unavailable reporting source." : null,
  };
}
